import React, { useState, useEffect } from 'react';
import { 
  X, Image as ImageIcon, FileText, Check, Loader2, 
  Calendar, HardDrive, Search, CheckSquare, Square, RefreshCw
} from 'lucide-react';
import { getMediaListForAi, MediaLibraryItem, formatFileSize, AI_MEDIA_RETENTION_NOTICE } from '../../services/mediaService';
import toast from 'react-hot-toast';

interface AiMediaLibraryModalProps {
  isOpen: boolean;
  onClose: () => void;
  alreadySelectedUrls?: string[];
  onConfirm: (selectedFiles: MediaLibraryItem[]) => void;
}

export const AiMediaLibraryModal: React.FC<AiMediaLibraryModalProps> = ({
  isOpen,
  onClose,
  alreadySelectedUrls = [],
  onConfirm,
}) => {
  // Tab state: 'doc' (Tài liệu) | 'image' (Hình ảnh)
  const [activeTab, setActiveTab] = useState<'doc' | 'image'>('doc');
  
  // Data caches for both tabs
  const [docFiles, setDocFiles] = useState<MediaLibraryItem[]>([]);
  const [imageFiles, setImageFiles] = useState<MediaLibraryItem[]>([]);
  const [loadingDocs, setLoadingDocs] = useState<boolean>(false);
  const [loadingImages, setLoadingImages] = useState<boolean>(false);
  
  // Selected files map: key is fileUrl, value is MediaLibraryItem
  // Allows selecting files across BOTH tabs simultaneously
  const [selectedMap, setSelectedMap] = useState<Map<string, MediaLibraryItem>>(new Map());
  
  // Local search query
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Initial load
  useEffect(() => {
    if (!isOpen) return;

    // Pre-populate already selected if needed
    const initialMap = new Map<string, MediaLibraryItem>();
    setSelectedMap(initialMap);

    fetchDocs();
    fetchImages();
  }, [isOpen]);

  const fetchDocs = async () => {
    setLoadingDocs(true);
    try {
      const docs = await getMediaListForAi('doc');
      setDocFiles(docs);
    } catch (err: any) {
      console.error('Lỗi tải danh sách tài liệu:', err);
      toast.error('Không thể tải danh sách tài liệu từ thư viện.');
    } finally {
      setLoadingDocs(false);
    }
  };

  const fetchImages = async () => {
    setLoadingImages(true);
    try {
      const imgs = await getMediaListForAi('image');
      setImageFiles(imgs);
    } catch (err: any) {
      console.error('Lỗi tải danh sách hình ảnh:', err);
      toast.error('Không thể tải danh sách hình ảnh từ thư viện.');
    } finally {
      setLoadingImages(false);
    }
  };

  if (!isOpen) return null;

  const toggleSelect = (item: MediaLibraryItem) => {
    setSelectedMap((prev) => {
      const next = new Map(prev);
      if (next.has(item.url)) {
        next.delete(item.url);
      } else {
        next.set(item.url, item);
      }
      return next;
    });
  };

  const handleSelectAllInCurrentTab = () => {
    const currentList = activeTab === 'doc' ? filteredDocs : filteredImages;
    const allSelected = currentList.every((item) => selectedMap.has(item.url));

    setSelectedMap((prev) => {
      const next = new Map(prev);
      if (allSelected) {
        currentList.forEach((item) => next.delete(item.url));
      } else {
        currentList.forEach((item) => next.set(item.url, item));
      }
      return next;
    });
  };

  const handleConfirmSelection = () => {
    const selectedList = Array.from(selectedMap.values());
    if (selectedList.length === 0) {
      toast('Chưa có file nào được chọn.', { icon: 'ℹ️' });
      return;
    }
    onConfirm(selectedList);
    onClose();
  };

  const filteredDocs = docFiles.filter((f) =>
    f.name.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const filteredImages = imageFiles.filter((f) =>
    f.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const currentList = activeTab === 'doc' ? filteredDocs : filteredImages;
  const isLoading = activeTab === 'doc' ? loadingDocs : loadingImages;
  const totalSelectedCount = selectedMap.size;

  const formatDate = (isoStr: string) => {
    try {
      const d = new Date(isoStr);
      return d.toLocaleDateString('vi-VN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return 'Vừa xong';
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-[110] flex items-center justify-center p-3 sm:p-4 backdrop-blur-xs animate-in fade-in duration-200">
      <div 
        className="bg-white rounded-2xl w-full max-w-4xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh] border border-slate-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between bg-white">
          <div className="flex items-center space-x-2.5">
            <div className="w-8 h-8 rounded-lg bg-purple-100 text-purple-700 flex items-center justify-center font-bold">
              📚
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-800">
                Thư viện tài nguyên phục vụ AI
              </h3>
              <p className="text-[11px] text-slate-400">
                Chọn tài liệu văn bản hoặc hình ảnh tĩnh đã có trong thư viện chung
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* 2 Tabs Header & Toolbar */}
        <div className="px-5 pt-3 pb-2.5 bg-slate-50 border-b border-slate-200/80 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          {/* 2 Tabs: Tài liệu & Hình ảnh */}
          <div className="flex items-center bg-slate-200/80 p-1 rounded-xl w-fit">
            <button
              type="button"
              onClick={() => setActiveTab('doc')}
              className={`flex items-center space-x-2 px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${
                activeTab === 'doc'
                  ? 'bg-white text-purple-700 shadow-xs'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <FileText className="w-3.5 h-3.5" />
              <span>Tài liệu (PDF, Word)</span>
              {docFiles.length > 0 && (
                <span className="ml-1 px-1.5 py-0.2 bg-purple-100 text-purple-800 rounded-full text-[10px]">
                  {docFiles.length}
                </span>
              )}
            </button>

            <button
              type="button"
              onClick={() => setActiveTab('image')}
              className={`flex items-center space-x-2 px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${
                activeTab === 'image'
                  ? 'bg-white text-purple-700 shadow-xs'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <ImageIcon className="w-3.5 h-3.5" />
              <span>Hình ảnh</span>
              {imageFiles.length > 0 && (
                <span className="ml-1 px-1.5 py-0.2 bg-purple-100 text-purple-800 rounded-full text-[10px]">
                  {imageFiles.length}
                </span>
              )}
            </button>
          </div>

          {/* Search & Actions */}
          <div className="flex items-center space-x-2">
            <div className="relative flex-1 sm:w-56">
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Tìm theo tên file..."
                className="w-full pl-8 pr-3 py-1.5 text-xs bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
              />
            </div>
            
            <button
              type="button"
              onClick={activeTab === 'doc' ? fetchDocs : fetchImages}
              disabled={isLoading}
              title="Tải lại danh sách"
              className="p-1.5 text-slate-500 hover:text-purple-600 bg-white border border-slate-200 hover:bg-purple-50 rounded-lg transition-colors"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} />
            </button>

            {currentList.length > 0 && (
              <button
                type="button"
                onClick={handleSelectAllInCurrentTab}
                className="text-xs text-purple-700 hover:text-purple-800 font-semibold px-2 py-1 hover:bg-purple-50 rounded-lg transition-colors whitespace-nowrap"
              >
                {currentList.every((item) => selectedMap.has(item.url))
                  ? 'Bỏ chọn tab này'
                  : 'Chọn tất cả tab này'}
              </button>
            )}
          </div>
        </div>

        {/* Notice Banner (Retention Policy 2.3) */}
        <div className="px-5 py-2 bg-amber-50/70 border-b border-amber-200/60 text-[11px] text-amber-800 flex items-center justify-between">
          <span>{AI_MEDIA_RETENTION_NOTICE}</span>
          <span className="font-semibold text-purple-700 hidden sm:inline">
            Đã chọn chéo cả 2 tab: {totalSelectedCount} file
          </span>
        </div>

        {/* Content Body */}
        <div className="flex-1 overflow-y-auto p-5 bg-slate-50/40">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center h-56 space-y-3">
              <Loader2 className="w-7 h-7 text-purple-600 animate-spin" />
              <p className="text-xs text-slate-500">
                Đang kết nối thư viện {activeTab === 'doc' ? 'tài liệu' : 'hình ảnh'}...
              </p>
            </div>
          ) : currentList.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-56 space-y-3 text-slate-400">
              {activeTab === 'doc' ? (
                <FileText className="w-12 h-12 opacity-40 text-slate-400" />
              ) : (
                <ImageIcon className="w-12 h-12 opacity-40 text-slate-400" />
              )}
              <p className="text-xs font-medium">
                {searchQuery
                  ? 'Không tìm thấy file phù hợp với từ khóa.'
                  : `Chưa có ${activeTab === 'doc' ? 'tài liệu' : 'hình ảnh'} nào trong thư viện.`}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {currentList.map((item) => {
                const isSelected = selectedMap.has(item.url);
                const isDoc = item.type === 'doc';

                return (
                  <div
                    key={item.url}
                    onClick={() => toggleSelect(item)}
                    className={`p-3 rounded-xl border-2 cursor-pointer transition-all flex flex-col justify-between group bg-white relative ${
                      isSelected
                        ? 'border-purple-600 bg-purple-50/30 ring-2 ring-purple-500/15 shadow-xs'
                        : 'border-slate-200 hover:border-purple-300 hover:shadow-xs'
                    }`}
                  >
                    {/* Header item: Icon & Checkbox */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center space-x-2.5 min-w-0">
                        {isDoc ? (
                          <div className="w-9 h-9 rounded-lg bg-rose-50 text-rose-600 flex items-center justify-center shrink-0 border border-rose-100">
                            <FileText className="w-4 h-4" />
                          </div>
                        ) : (
                          <div className="w-10 h-10 rounded-lg overflow-hidden bg-slate-100 shrink-0 border border-slate-200">
                            <img
                              src={item.url}
                              alt={item.name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                (e.target as HTMLElement).style.display = 'none';
                              }}
                            />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-bold text-slate-800 truncate" title={item.name}>
                            {item.name}
                          </p>
                          <div className="flex items-center space-x-2 mt-0.5 text-[10px] text-slate-400">
                            <span className="flex items-center space-x-0.5">
                              <HardDrive className="w-2.5 h-2.5" />
                              <span>{item.sizeFormatted || formatFileSize(item.size)}</span>
                            </span>
                            <span>•</span>
                            <span className="flex items-center space-x-0.5">
                              <Calendar className="w-2.5 h-2.5" />
                              <span>{formatDate(item.createdAt)}</span>
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="shrink-0 pt-0.5">
                        {isSelected ? (
                          <div className="w-5 h-5 rounded bg-purple-600 text-white flex items-center justify-center shadow-xs">
                            <Check className="w-3.5 h-3.5" />
                          </div>
                        ) : (
                          <div className="w-5 h-5 rounded border border-slate-300 group-hover:border-purple-400 transition-colors" />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between bg-white">
          <div className="text-xs text-slate-600 font-medium">
            {totalSelectedCount > 0 ? (
              <span>
                Đã chọn <strong className="text-purple-700 font-bold">{totalSelectedCount} file</strong> từ cả 2 tab
              </span>
            ) : (
              <span className="text-slate-400">Chưa chọn file nào</span>
            )}
          </div>

          <div className="flex items-center space-x-2.5">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs font-semibold text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition-colors"
            >
              Hủy
            </button>
            <button
              type="button"
              onClick={handleConfirmSelection}
              disabled={totalSelectedCount === 0}
              className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50 disabled:pointer-events-none text-white text-xs font-bold rounded-xl shadow-md transition-all flex items-center space-x-1.5"
            >
              <Check className="w-4 h-4" />
              <span>Xác nhận chọn ({totalSelectedCount})</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
